import React, { useEffect, useRef, useState } from "react";
import { Alert, Col, Form, Row, Tab, Tabs } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams, useRouteLoaderData } from "react-router-dom";

import { useAppApi } from "@/App";
import { useRequest } from "@components/AppHook";
import VkVideoUploader from "@components/Common/VKVideo/VKVideoUploader";
import VkVideoPlayer from "@components/Common/VkVideoPlayer";
import { DevForm, FormFieldGroup } from "@components/Forms/Fields";
import { useSubmit } from "@components/Forms/Handlers";
import { Awesome } from "@components/Helpers";
import { CommonCard } from "@components/Pages";
import { BackendPromise, url_reverse } from "@components/Utils";
import List from "@screens/Game/Highlights/components/List";
import ListDraft from "@screens/Game/Highlights/components/ListDraft";

export default function Highlights(props) {
    const params = useParams();

    if (params.id) {
        // Добавление скриншот / видео
        props = { ...props, id: params.id };
        return <HighlightsEdit {...props} />;
    }

    return <HighlightsList {...props} />;
}

/**
 * apiData.use_draft - могут ли использоваться черновики (для супер админа)
 * apiData.drafts - количество черновиков
 */
function HighlightsList(props) {
    const { gameId, curLoc, apiData, setApiData, apiIsLoading } = props;
    const { t } = useTranslation();
    const [activeKey, setActiveKey] = useState("list");
    const navigate = useNavigate();
    const defLocData = { ru_RU: [], en_US: [] };
    const [idsToDelete, setIdsToDelete] = useState(defLocData);
    const [idsToDeleteDraft, setIdsToDeleteDraft] = useState(defLocData);
    const { api_post } = useRequest();
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const commonInfo = useRouteLoaderData("commonInfo");
    const hlData = apiData?.hl_data;
    const location = useLocation();
    const pathParts = location.pathname.split("/");
    const backNavigate = pathParts.includes("locales") ? "locales" : "admin";

    const deleteToggleHandle = (e, draft = false) => {
        if (draft) {
            const id = Number(e.target.value);
            const newIdsToDelete = { ...idsToDeleteDraft };
            if (newIdsToDelete[curLoc].includes(id)) {
                newIdsToDelete[curLoc] = newIdsToDelete[curLoc].filter((i) => i !== id);
            } else {
                newIdsToDelete[curLoc].push(id);
            }
            setIdsToDeleteDraft(newIdsToDelete);
        } else {
            const id = Number(e.target.value);
            const newIdsToDelete = { ...idsToDelete };
            if (newIdsToDelete[curLoc].includes(id)) {
                newIdsToDelete[curLoc] = newIdsToDelete[curLoc].filter((i) => i !== id);
            } else {
                newIdsToDelete[curLoc].push(id);
            }
            setIdsToDelete(newIdsToDelete);
        }
    };

    const bulkDeleteHandle = (ev) => {
        api_post(ev, url_reverse("api:game:highlights:bulk_delete", { game_id: gameId, curLoc }), {
            params: {
                ids: idsToDelete[curLoc],
                draft_ids: idsToDeleteDraft[curLoc],
                locl: curLoc,
            },
            then: (resp) => {
                if (resp.status === "ok" && idsToDelete[curLoc]) {
                    const newHlData = hlData;
                    newHlData[curLoc].highlights_left =
                        newHlData[curLoc].highlights_count - idsToDelete[curLoc].length >
                        newHlData[curLoc].highlights_limit
                            ? 0
                            : newHlData[curLoc].highlights_left + idsToDelete[curLoc].length;
                    newHlData[curLoc].highlights_count = hlData[curLoc].highlights_count - idsToDelete[curLoc].length;

                    // Убираем из отображения удаленные скрины
                    newHlData.object_list[curLoc] = hlData.object_list[curLoc].filter(
                        (o) => !idsToDelete[curLoc].includes(o.id)
                    );
                    newHlData.object_list_draft[curLoc] = hlData.object_list_draft[curLoc].filter(
                        (o) => !idsToDeleteDraft[curLoc].includes(o.id)
                    );
                    setApiData({ ...apiData, newHlData });
                    setIdsToDelete(defLocData);
                    setIdsToDeleteDraft(defLocData);
                }
            },
        });
    };
    // const showAddButton
    const showAddButton =
        (!apiData.lock?.is_locked && !gameCommonInfo.game.locked_by_moderation_states) || commonInfo.account.is_super;
    if (!hlData) return;
    return (
        <div>
            <div className="dmg-callout dmg-callout-info mb-3">
                {gameCommonInfo.game.base_locale === curLoc ? (
                    <ul className="mb-0 pl-3">
                        <li>{t("Добавьте не менее 6 скриншотов в базовую локализацию.")}</li>
                        <li>
                            {t(
                                "Расположение скриншотов и видео в кабинете и на странице совпадает. " +
                                    "При необходимости задайте нужный порядок."
                            )}
                        </li>
                        <li>{t("Видео рекомендуется размещать первым.")}</li>
                    </ul>
                ) : (
                    <div>
                        {t(
                            "При отсутствии скриншотов для небазовой локализации будут использоваться скриншоты " +
                                "из базовой локализации. Чтобы добавить локализованные скриншоты, необходимо " +
                                "загрузить минимум 6 изображений."
                        )}
                    </div>
                )}
                <hr />
                <div>
                    {t("Максимум медиафайлов")}: {apiData?.hl_data[curLoc].highlights_limit}
                </div>
                <div>
                    {t("Осталось медиафайлов")}: {apiData?.hl_data[curLoc].highlights_left}
                </div>
            </div>

            <Alert variant="danger" className="mb-3">
                <strong>{t("Внимание! На скриншотах и видео не должно быть:")}</strong>
                <br />❌ {t("ненормативной лексики")};
                <br />❌ {t("табака и процесса курения")};
                <br />❌ {t("алкоголя")};
                <br />❌ {t("сцен насилия, провокационного и сексуального контента (18+)")}.
            </Alert>

            <div className="d-flex justify-content-end">
                {/*Отображаем кнопку только если не в локе и не на модерации или СУ*/}
                {showAddButton && (
                    <a
                        className="btn btn-sm btn-dark ml-1"
                        onClick={() =>
                            navigate(
                                url_reverse(
                                    `games:highlights:${apiData.use_drafts ? "draft:" : ""}add`,
                                    {
                                        game_id: gameId,
                                        locale: curLoc,
                                    },
                                    { back_navigate: backNavigate }
                                )
                            )
                        }
                    >
                        <Awesome type="plus-circle" /> {t("Добавить")}
                    </a>
                )}
                {/*Кнопка "Удалить" */}
                {(idsToDelete[curLoc]?.length > 0 || idsToDeleteDraft[curLoc]?.length > 0) && (
                    <DevForm.Button className="btn-sm ml-1 btn-danger" name="bulk_delete" onClick={bulkDeleteHandle}>
                        <Awesome type="trash" /> {t("Удалить")}
                    </DevForm.Button>
                )}
            </div>
            <Tabs
                activeKey={activeKey}
                transition={false}
                className="react-bootstrap-tabs-fix"
                onSelect={(k) => setActiveKey(k || "list")}
            >
                <Tab eventKey="list" title={t("Опубликованные")}>
                    <List
                        apiData={apiData}
                        setApiData={setApiData}
                        gameId={gameId}
                        locl={curLoc}
                        deleteToggleHandle={deleteToggleHandle}
                        idsToDelete={idsToDelete[curLoc]}
                    />
                </Tab>
                {hlData.drafts > 0 && (
                    <Tab eventKey="draft" title={t("Черновики")}>
                        <ListDraft
                            apiData={apiData}
                            isLoading={apiIsLoading}
                            locl={curLoc}
                            deleteToggleHandle={deleteToggleHandle}
                            idsToDelete={idsToDeleteDraft[curLoc]}
                        />
                    </Tab>
                )}
            </Tabs>
        </div>
    );
}

function HighlightsEdit({ gameId, locl, header, id, isDraftUrl }) {
    const { t } = useTranslation();
    const { apiData, setApiData } = useAppApi(
        url_reverse(`api:game:highlights:${isDraftUrl ? "draft:" : ""}edit`, { game_id: gameId, locl, id })
    );
    const onFormSubmit = useSubmit();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [highlightType, setHighlightType] = useState("screenshot");
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const backNavigate = queryParams.get("back_navigate");

    let backUrl = null;
    if (isDraftUrl) {
        backUrl = url_reverse(
            `games:highlights:draft:add`,
            {
                game_id: gameId,
                locale: locl,
            },
            { back_navigate: backNavigate }
        );
    } else {
        backUrl = url_reverse(
            `games:highlights:add`,
            {
                game_id: gameId,
                locale: locl,
            },
            { back_navigate: backNavigate }
        );
    }

    const btnRef = useRef(null);
    const [files, setFiles] = useState([]);
    const [done, setDone] = useState(true);
    const [isShowAlert, setIsShowAlert] = useState(false);

    let backNavigateUrl = null;

    // либо в Администрирование либо Основные свойства
    if (backNavigate === "locales") {
        backNavigateUrl = url_reverse("games:locale:edit", { locale: locl, game_id: gameId });
    } else {
        backNavigateUrl = url_reverse("games:admin", { game_id: gameId });
    }

    useEffect(() => {
        setDone(false);
        setIsShowAlert(
            Boolean(files?.length && apiData?.forms_data?.main?.data?.pic?.length > apiData?.highlights_left)
        );
        setFiles(apiData?.forms_data?.main?.data?.pic);
    }, [apiData]);

    useEffect(() => {
        // При переключении типов сбрасываем данные, чтобы не была возможность заполнить все поля и запутаться в типах
        const newData = { ...apiData };
        if (newData.forms_data?.main?.data) {
            newData.forms_data.main.data = {};
            setApiData(newData);
        }
    }, [highlightType]);

    useEffect(() => {
        const newData = { ...apiData };
        if (newData?.forms_data?.main) {
            newData.forms_data.main.fields.pic.disabled = apiData.disable_upload;
            newData.forms_data.main.fields.vkvideo_file.disabled = apiData.disable_upload;
        }
        setApiData(newData);
    }, [apiData?.forms_data]);

    if (!apiData) return;

    const uploadHandle = () => {
        setIsLoading(true);
        const uploadResult = files.reverse().map(async (file, index) => {
            const formData = new FormData();
            formData.append("main-pic", file);
            formData.append("main-priority", apiData.min_priority + index);

            return BackendPromise.post_real_formdata(
                url_reverse(`api:game:highlights:${isDraftUrl ? "draft:" : ""}add`, { game_id: gameId, locl }),
                formData
            ).then((resp) => {
                const newFiles = [...files];
                if (resp.status === "fail") {
                    newFiles[index].status = "fail";
                    newFiles[index].message =
                        resp.forms_data?.main?.errors?.pic || resp.forms_data?.main?.errors?.__all__;
                } else {
                    newFiles[index].status = "ok";
                    newFiles[index].message = t("Успешно загружен");
                }
                setFiles(newFiles);

                return { ...resp, index };
            });
        });

        Promise.all(uploadResult)
            .then((results) => {
                const successCount = results.filter((result) => result.status === "ok").length;
                const newData = { ...apiData };
                newData.highlights_left = apiData.highlights_left - successCount;
                // Обновляем min_priority, чтобы при следующей загрузке приоритеты не дублировались
                newData.min_priority = apiData.min_priority + successCount;
                setApiData(newData);
            })
            .finally(() => {
                setIsLoading(false);
                setDone(true);
                setFiles([]);
            });
    };

    const updateFileList = () => {
        setFiles([]);
        setIsShowAlert(false);
    };

    const checkAlert = () => {
        return (
            <>
                <Alert variant="danger">
                    {t("Доступно")}: {apiData.highlights_left}. {t("Загружено")}: {files?.length}{" "}
                </Alert>
            </>
        );
    };

    return (
        <CommonCard header={header}>
            {isDraftUrl && (
                <Alert variant="info">{apiData?.lock_status ? apiData?.lock_status : t("Добавление черновика")}</Alert>
            )}
            {isShowAlert && checkAlert()}
            <Form onSubmit={(ev) => onFormSubmit(ev, { backUrl })}>
                <FormFieldGroup label={t("Тип")}>
                    <DevForm.Select
                        clearable={false}
                        onChange={(ev) => setHighlightType(ev.value)}
                        defaultValue={{ value: "screenshot", label: t("Скриншот") }}
                        choices={[
                            { value: "screenshot", label: t("Скриншот") },
                            { value: "video_link", label: t("Ссылка на VK Видео") },
                            { value: "video_direct", label: t("Видеофайл") },
                        ]}
                    />
                </FormFieldGroup>

                {highlightType === "screenshot" && (
                    <>
                        <Row>
                            <Col sm={3} className="my-auto" />
                            <Col sm={9}>
                                <ul className="mb-3 pl-0">
                                    {files &&
                                        files.map((file, index) => (
                                            <li key={`file-${index}`}>
                                                <span>{file.name}</span>
                                                {file?.message && (
                                                    <Alert
                                                        variant={file?.status === "fail" ? "danger" : "success"}
                                                        className="mt-1"
                                                    >
                                                        {file.message}
                                                    </Alert>
                                                )}
                                            </li>
                                        ))}
                                </ul>
                            </Col>
                        </Row>
                        <DevForm.Field prefix="main" name="pic" onClick={updateFileList} />
                    </>
                )}
                {highlightType === "video_link" && <DevForm.Field prefix="main" name="video" />}

                {highlightType === "video_direct" && (
                    <>
                        <DevForm.Field prefix="main" name="vkvideo_file" />

                        {apiData?.vkvideo_data && (
                            <Row>
                                <Col sm="3" />
                                <Col sm="9" className="my-auto">
                                    <VkVideoPlayer data={apiData.vkvideo_data} height="300" />
                                </Col>
                            </Row>
                        )}

                        <VkVideoUploader
                            apiData={apiData}
                            setApiData={setApiData}
                            btnRef={btnRef}
                            urlForGetUploadLink={url_reverse("api:game:highlights:vkvideo_upload_link", {
                                game_id: gameId,
                                locl,
                            })}
                        />
                    </>
                )}

                <div className="d-flex">
                    {highlightType !== "screenshot" && (
                        <DevForm.Button
                            variant="primary"
                            type="submit"
                            name="save"
                            disabled={isLoading}
                            innerRef={btnRef}
                        >
                            {t("Сохранить")}
                        </DevForm.Button>
                    )}

                    <a onClick={() => navigate(`${backNavigateUrl}#highlights`)} className="btn btn-light ml-2">
                        {t("Назад")}
                    </a>

                    {highlightType === "screenshot" && files && !done && (
                        <DevForm.Button
                            className="ml-3"
                            name="upload"
                            isLoading={isLoading}
                            onClick={uploadHandle}
                            disabled={files.length > apiData.highlights_left}
                        >
                            {t("Загрузить")}
                        </DevForm.Button>
                    )}
                </div>
            </Form>
        </CommonCard>
    );
}
